// Copyright (c) 2023, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 Inc. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.

import * as React from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import BackdropProgress from '../../BackdropProgress';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClearIcon from "@material-ui/icons/Clear";
import CustomSnackbar from '../ui/Snackbars';
import EnhancedTableHead from '../ui/EnhancedTableHead';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import Grid from '@mui/material/Grid'
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import SearchIcon from "@mui/icons-material/Search";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import TravelInfoCards from './TravelInfoCards';
import useHttp from '../../utils/http';
import wso2Theme from '../../theme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { adjustForTimezone, getDateForDB, getCountryList, truncateString, getComparator, stableSort } from '../../utils/formatting';
import { travelStatus } from '../../utils/const';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { getEmployeeEmails } from '../../utils/oauth';
import { postSearchTravelRequests, getTravelAgents } from '../../utils/end-points.js';
import { getTravelStatusColor, getTravelStatusFontColor } from '../../theme/status-colors';
import { IconButton } from "@material-ui/core";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useEffect, useState, useCallback } from 'react';
import "../../App.css";
import "../../css/NewTravelRequests.scss"

const headCells = [
    { id: 'traveller_email', label: "Traveller's Email", align: 'left', disablePadding: false, sortable: true },
    { id: 'country', label: 'Destination Country', align: 'left', disablePadding: false, sortable: true },
    { id: 'start_date', label: 'Start Date', align: 'left', disablePadding: false, sortable: true },
    { id: 'end_date', label: 'End Date', align: 'left', disablePadding: false, sortable: true },
];

const NewTravelRequests = () => {

    const employees = getEmployeeEmails()
    const [filtersExpanded, setFiltersExpanded] = useState(false)
    const [order, setOrder] = useState('desc')
    const [orderBy, setOrderBy] = useState('start_date')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [travelRequests, setTravelRequests] = useState([])
    const [activeTravelAgents, setActiveTravelAgents] = useState(null)
    const [emailFilter, setEmailFilter] = useState(null)
    const [countryFilter, setCountryFilter] = useState(null)
    const [searchFilter, setSearchFilter] = useState("")
    const [selectedTravel, setSelectedTravel] = useState(null)
    const [isPageLoading, setPageLoading] = useState(true)
    const [isTableLoading, setTableLoading] = useState(false)
    const [isTablePaginating, setTablePaginating] = useState(false)
    const [startDateFilter, setStartDateFilter] = useState(null)
    const [endDateFilter, setEndDateFilter] = useState(null)
    const [snackbarData, setSnackbarData] = useState({ open: false, message: "", severity: "", onClose: null })
    const { handleRequest } = useHttp();

    useEffect(() => {
        fetchTravelAgents()
    }, [])

    useEffect(() => {
        if (window.innerHeight >= 900) {
            setRowsPerPage(14)
        }
        else if (window.innerHeight >= 850) {
            setRowsPerPage(13)
        }
    }, [window.innerHeight])

    useEffect(() => {
        fetchTravels()
    }, [rowsPerPage, emailFilter, countryFilter, startDateFilter, endDateFilter, orderBy, order])

    const fetchTravelAgents = useCallback(async () => {
        setPageLoading(true)
        handleRequest(getTravelAgents + "?status=active", "GET", null, (data) => {
            if (data) {
                setActiveTravelAgents(data)
            }
            else {
                setSnackbarData({ message: "Error while fetching travel agents", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setPageLoading(false)
        }, (error) => {
            setPageLoading(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }, [])

    const fetchTravels = useCallback(async () => {
        setTableLoading(true)

        let body = {
            status: travelStatus.NEW,
            limit: rowsPerPage,
            offset: 0,
            searchString: searchFilter,
            email: emailFilter?.workEmail,
            country: countryFilter?.country,
            startDate: getDateForDB(startDateFilter),
            endDate: getDateForDB(endDateFilter),
            sortColumn: orderBy,
            sortOrder: order
        }

        handleRequest(postSearchTravelRequests, "POST", body, (travels) => {
            if (travels) {
                setTravelRequests(travels)
                setSelectedTravel(travels[0])
                setPage(0)
            }
            setTableLoading(false)
        }, (error) => {
            setTableLoading(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }, [rowsPerPage, searchFilter, emailFilter, countryFilter, startDateFilter, endDateFilter, orderBy, order])

    const loadMoreTravels = (offset) => {
        setTablePaginating(true)

        let body = {
            status: travelStatus.NEW,
            limit: rowsPerPage,
            offset: offset,
            searchString: searchFilter,
            email: emailFilter?.workEmail,
            country: countryFilter?.country,
            startDate: getDateForDB(startDateFilter),
            endDate: getDateForDB(endDateFilter),
            sortColumn: orderBy,
            sortOrder: order
        }

        handleRequest(postSearchTravelRequests, "POST", body, (travels) => {
            if (travels) {
                let travelsList = travelRequests.concat(travels)
                setTravelRequests(travelsList)
                if (travels.length === 0) {
                    setPage(page => page - 1)
                    setSnackbarData({ message: "No more data", open: true, severity: "success", onClose: onCloseSnackbar })
                }
            }
            else {
                setSnackbarData({ message: "Error while fetching travels", open: true, severity: "error", onClose: onCloseSnackbar })
            }
            setTablePaginating(false)
        }, (error) => {
            setTablePaginating(false)
            setSnackbarData({ message: error, open: true, severity: "error", onClose: onCloseSnackbar })
        })
    }

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    }

    const handleClick = (travel) => {
        setSelectedTravel(travel)
    }

    const handleChangePage = (event, newPage) => {
        if (travelRequests.length <= newPage * rowsPerPage) {
            loadMoreTravels(travelRequests.length)
        }
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0)
    }

    const handleEmailFilter = (event, value) => {
        setEmailFilter(value)
    }

    const handleCountryFilter = (event, value) => {
        setCountryFilter(value)
    }

    const handleSearchFilter = (event) => {
        setSearchFilter(event.target.value)
        if (!event.target.value) {
            fetchTravels()
        }
    }

    const submitFormCallback = () => {
        fetchTravels()
    }

    const onCloseSnackbar = () => {
        setSnackbarData({ ...snackbarData, open: false })
    }

    return (

        <React.Fragment>

            <CustomSnackbar snackbar={snackbarData} />

            <BackdropProgress open={isPageLoading} />

            <div className='New-travel-requests'>
                <div className='Page-title'>New Travel Requests
                    <span className='Status-chip'
                        style={{
                            backgroundColor: getTravelStatusColor(travelStatus.NEW),
                            color: getTravelStatusFontColor(travelStatus.NEW)
                        }}>
                        {travelStatus.NEW}
                    </span>
                </div>

                {!isPageLoading && (

                    <Grid container>
                        <Grid item lg={8} md={7} xs={12} className='Table-grid'>
                            <Paper className='Border-elevation'>

                                <div className='Search-and-filters' style={{ borderColor: wso2Theme.palette.primary.light }}>
                                    <div className="Table-search-field">
                                        <TextField variant="outlined" label="Search" size="small"
                                            className='Search-text-fields'
                                            onChange={handleSearchFilter}
                                        />
                                        <Button variant={"contained"} startIcon={<SearchIcon />} color="secondary" className='Filter-button'
                                            onClick={fetchTravels} >Search</Button>
                                        <Button variant={filtersExpanded ? "contained" : "outlined"} startIcon={<FilterAltIcon />} color="primary" className='Filter-button'
                                            onClick={() => setFiltersExpanded(!filtersExpanded)} >{filtersExpanded ? "Hide Filters" : "Show Filters"}</Button>
                                    </div>
                                    {filtersExpanded && (
                                        <div className='Filters-div'>
                                            <Grid container>
                                                <Grid item lg={3} md={6} className='Filter-grid-item-email'>
                                                    <Autocomplete
                                                        size="small"
                                                        variant="standard"
                                                        noOptionsText="No options"
                                                        value={emailFilter}
                                                        disablePortal
                                                        options={employees.filter(emp => emp.workEmail !== null)}
                                                        getOptionLabel={(option) => option.workEmail}
                                                        onChange={handleEmailFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props} key={option.workEmail}>{option.workEmail}</Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Traveller's Email" />}
                                                    />
                                                </Grid>
                                                <Grid item lg={3} md={6} className='Filter-grid-item-country'>
                                                    <Autocomplete
                                                        size="small"
                                                        disablePortal
                                                        value={countryFilter}
                                                        options={getCountryList()}
                                                        getOptionLabel={(option) => option.country}
                                                        onChange={handleCountryFilter}
                                                        renderOption={(props, option) => (
                                                            <Box {...props}>{option.country}</Box>
                                                        )}
                                                        renderInput={(params) => <TextField  {...params} label="Country" />}
                                                    />
                                                </Grid>
                                                <Grid item lg={3} md={6} className='Filter-grid-item-start-date'>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <DatePicker
                                                            label="Start Date"
                                                            inputFormat='yyyy-MM-dd'
                                                            mask='____-__-__'
                                                            value={startDateFilter}
                                                            onChange={(newValue) => {
                                                                setStartDateFilter(newValue);
                                                            }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <IconButton onClick={() => setStartDateFilter(null)} size="small">
                                                                        <ClearIcon />
                                                                    </IconButton>
                                                                )
                                                            }}
                                                            InputAdornmentProps={{
                                                                position: "start"
                                                            }}
                                                            renderInput={(params) => <TextField size="small" {...params}
                                                                fullWidth />}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                                <Grid item lg={3} md={6} className='Filter-grid-item-end-date'>
                                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                                        <DatePicker
                                                            label="End Date"
                                                            inputFormat='yyyy-MM-dd'
                                                            mask='____-__-__'
                                                            value={endDateFilter}
                                                            onChange={(newValue) => {
                                                                setEndDateFilter(newValue);
                                                            }}
                                                            InputProps={{
                                                                endAdornment: (
                                                                    <IconButton onClick={() => setEndDateFilter(null)} size="small">
                                                                        <ClearIcon />
                                                                    </IconButton>
                                                                )
                                                            }}
                                                            InputAdornmentProps={{
                                                                position: "start"
                                                            }}
                                                            renderInput={(params) => <TextField size="small" {...params}
                                                                fullWidth />}
                                                        />
                                                    </LocalizationProvider>
                                                </Grid>
                                            </Grid>
                                        </div>
                                    )}
                                </div>

                                {isTableLoading && <LinearProgress color='secondary' />}
                                <TableContainer>

                                    <Table aria-labelledby="tableTitle" size={'small'} >
                                        <EnhancedTableHead headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                                        <TableBody>
                                            {selectedTravel && stableSort(travelRequests, getComparator(order, orderBy))
                                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                                .map((row) => {
                                                    return (
                                                        <TableRow onClick={(event) => handleClick(row)} tabIndex={-1} key={row.jobNumber}
                                                            className={selectedTravel.jobNumber === row.jobNumber ? 'Selected-row' : "Table-row"}
                                                            style={{
                                                                backgroundColor: selectedTravel.jobNumber === row.jobNumber ? "white" : null,
                                                                elevation: selectedTravel.jobNumber === row.jobNumber ? "above" : null,
                                                            }}>
                                                            <TableCell className='Table-cell' >{truncateString(row.travellerEmail, 20)}</TableCell>
                                                            <TableCell className='Table-cell' >{truncateString(row.country, 20)}</TableCell>
                                                            <TableCell className='Table-cell' >{getDateForDB(adjustForTimezone(new Date(row.startDate)))}</TableCell>
                                                            <TableCell className='Table-cell' >{getDateForDB(adjustForTimezone(new Date(row.endDate)))}</TableCell>
                                                        </TableRow>

                                                    );
                                                })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {!isTableLoading && !isTablePaginating && travelRequests.length === 0 && <div className='No-requests-label'>No New Travel Requests</div>}
                                {(isTableLoading || isTablePaginating) && <div className='No-requests-label'>Please wait ...</div>}
                                {isTablePaginating && <LinearProgress color='secondary' />}

                                <TablePagination
                                    className='Table-pagination'
                                    rowsPerPageOptions={window.innerHeight >= 900 ? [10, 14, 25] : window.innerHeight >= 850 ? [10, 13, 25] : [10, 10, 25]}
                                    component="div"
                                    count={-1}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                />
                            </Paper>
                        </Grid>
                        {selectedTravel ?
                            <Grid item lg={4} md={5} xs={12} className='Details-grid'>
                                <TravelInfoCards 
                                    travellerEmail={selectedTravel.travellerEmail} 
                                    jobNumber={selectedTravel.jobNumber}
                                    startDate={selectedTravel.startDate}
                                    endDate={selectedTravel.endDate}
                                    travelAgents={activeTravelAgents}
                                    submitCallback={() => { submitFormCallback() }}
                                    setParentLoading={(isPageLoading) => { setPageLoading(isPageLoading) }}
                                    setParentSnackbar={(message, severity) => { setSnackbarData({ message: message, open: true, severity: severity, onClose: onCloseSnackbar }) }} />
                            </Grid> : null}
                    </Grid>
                )}
            </div>
        </React.Fragment>
    );
}

export default NewTravelRequests;